import React from 'react';
import './DateTimeParts.css';

/**
 * A wall-clock date and time as two boxes, for forms that keep the value as `YYYY-MM-DDTHH:mm[:ss]` ('' when
 * either part is empty). Seconds in the original value survive until the time itself is edited.
 */
export default function DateTimeParts({ id, legend, dateLabel, timeLabel, value, onChange, disabled = false, invalid = false, describedBy, children }: {
  id: string; legend: string; dateLabel: string; timeLabel: string; value: string; onChange: (value: string) => void;
  disabled?: boolean; invalid?: boolean; describedBy?: string; children?: React.ReactNode;
}) {
  const [date, setDate] = React.useState(() => value.slice(0, 10));
  const [time, setTime] = React.useState(() => value.slice(11));
  const emitted = React.useRef(value);
  // A value set from outside (cleared or restored) replaces both parts; our own edits do not echo back.
  React.useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setDate(value.slice(0, 10));
    setTime(value.slice(11));
  }, [value]);
  const emit = (nextDate: string, nextTime: string) => {
    const next = nextDate && nextTime ? `${nextDate}T${nextTime.length === 5 ? `${nextTime}:00` : nextTime}` : '';
    emitted.current = next;
    onChange(next);
  };
  return <fieldset className="date-time-parts" disabled={disabled}>
    <legend>{legend}</legend>
    <div className="date-time-parts__inputs">
      <input id={`${id}-date`} type="date" aria-label={dateLabel} value={date} aria-invalid={invalid || undefined} aria-describedby={describedBy}
        onChange={event => { setDate(event.target.value); emit(event.target.value, time); }} />
      <input id={`${id}-time`} type="time" aria-label={timeLabel} value={time.slice(0, 5)} aria-invalid={invalid || undefined} aria-describedby={describedBy}
        onChange={event => { setTime(event.target.value); emit(date, event.target.value); }} />
    </div>
    {children}
  </fieldset>;
}
